import { useState } from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ButtonLoadingIcon from "components/common/ButtonLoadingIcon";
import { submitLead, type LeadSource } from "services/leads.service";
import { HAS_BOOKING_PAGE, getBookingHref } from "content/showcase";
import { accentRedContainedButtonSx, showcaseSx } from "styles/main_style";

const NEEDS = [
  "AI chat assistant",
  "Lead capture funnel",
  "CRM or database integration",
  "Automated follow-up",
  "Appointment booking",
  "Voice AI agent",
  "Something else",
];

const TIMELINES = ["Right away", "This month", "This quarter", "Just exploring"];

type LeadFormProps = {
  source?: LeadSource;
  variant?: "demo" | "inquiry";
};

export default function LeadForm({
  source = "funnel-form",
  variant = "inquiry",
}: LeadFormProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [need, setNeed] = useState(NEEDS[0]);
  const [timeline, setTimeline] = useState(TIMELINES[0]);
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentMessage, setSentMessage] = useState<string | null>(null);

  const canSubmit = name.trim().length > 0 && /.+@.+\..+/.test(email.trim());

  const handleSubmit = async () => {
    if (!canSubmit) {
      setError("Add your name and a valid email so I can reply.");
      return;
    }

    setError(null);
    setIsSending(true);

    try {
      const result = await submitLead({
        name: name.trim(),
        email: email.trim(),
        company: company.trim(),
        need,
        timeline,
        message: message.trim(),
        source,
        website,
      });
      setSentMessage(result.message);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Unable to send that right now.");
    } finally {
      setIsSending(false);
    }
  };

  if (sentMessage) {
    return (
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <CheckCircleOutlineIcon sx={{ color: "#15803d" }} />
          <Typography sx={showcaseSx.stepLabel}>{sentMessage}</Typography>
        </Stack>
        {variant === "inquiry" ? null : (
          <Typography sx={showcaseSx.cardBody}>
            A confirmation email is on its way. If you would rather skip the email
            thread, {HAS_BOOKING_PAGE ? "pick a time directly." : "email me and we will pick a time."}
          </Typography>
        )}
        <Button
          href={getBookingHref()}
          target={HAS_BOOKING_PAGE ? "_blank" : undefined}
          rel={HAS_BOOKING_PAGE ? "noopener noreferrer" : undefined}
          variant="contained"
          size="large"
          sx={[{ textTransform: "none", fontWeight: 700 }, accentRedContainedButtonSx]}
        >
          {HAS_BOOKING_PAGE ? "Book a 30-minute call" : "Email to book a time"}
        </Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={2} sx={{ position: "relative" }}>
      {error ? <Alert severity="error">{error}</Alert> : null}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          label="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          fullWidth
        />
        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          fullWidth
        />
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          label="Company (optional)"
          value={company}
          onChange={(event) => setCompany(event.target.value)}
          fullWidth
        />
        <TextField
          select
          label="What do you need built?"
          value={need}
          onChange={(event) => setNeed(event.target.value)}
          fullWidth
        >
          {NEEDS.map((item) => (
            <MenuItem key={item} value={item}>
              {item}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <TextField
        select
        label="Timeline"
        value={timeline}
        onChange={(event) => setTimeline(event.target.value)}
        fullWidth
      >
        {TIMELINES.map((item) => (
          <MenuItem key={item} value={item}>
            {item}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        label="What are you trying to automate? (optional)"
        multiline
        minRows={variant === "inquiry" ? 2 : 3}
        value={message}
        onChange={(event) => setMessage(event.target.value)}
        fullWidth
      />

      <Box
        component="input"
        // Honeypot: hidden from people, tempting to bots.
        aria-hidden="true"
        tabIndex={-1}
        autoComplete="off"
        name="website"
        value={website}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          setWebsite(event.target.value)
        }
        sx={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
      />

      <Button
        variant="contained"
        size="large"
        disabled={isSending}
        onClick={() => void handleSubmit()}
        startIcon={isSending ? <ButtonLoadingIcon /> : null}
        sx={[
          {
            textTransform: "none",
            fontWeight: 700,
            alignSelf: variant === "inquiry" ? "stretch" : { xs: "stretch", sm: "flex-start" },
          },
          accentRedContainedButtonSx,
        ]}
      >
        {isSending ? "Sending..." : variant === "inquiry" ? "Send inquiry" : "Send it through the pipeline"}
      </Button>

      {variant === "inquiry" ? null : (
        <Typography sx={showcaseSx.codeCaption}>
          Submitting runs the real pipeline: stored in Supabase, pushed to n8n, and
          answered by an automated email.
        </Typography>
      )}
    </Stack>
  );
}
